import type { CurrencyPresentationResponse } from "@/lib/types";
import { resolveCurrencyPresentation } from "./resolve";
import type { CurrencyPresentation, CurrencyPresentationSettings } from "./types";

// SM-978 — a broker's own GOLD image, drawn for its users.
//
// The registry (`/api/currencies`, Backoffice → Currencies) stays the one
// source of every currency's name, symbol, colour and decimals. A user
// whose broker — or the nearest ancestor broker — set a GOLD image gets
// that image on GOLD and nothing else; RED and GREEN never change.
//
// Which image, nearest first, and the same order again when one fails to
// load in the browser:
//   1. the broker's image (`/api/me/currency-presentation`),
//   2. the registry's GOLD image,
//   3. the bundled gold coin,
//   4. only then the monogram.
// The seed table in resolve.ts is the cross-app parity contract and is
// not touched: the broker image is an overlay on the settings, and the
// fallbacks are computed from the platform settings beside it.

/** The broker's GOLD image URL, or null for the platform's. */
export function brokerGoldImageOf(overlay: CurrencyPresentationResponse | null | undefined): string | null {
  const url = overlay?.GOLD?.image_url;
  return typeof url === "string" && url !== "" ? url : null;
}

/** Platform settings with the broker's GOLD image on top (icon only). */
export function withBrokerOverlay(
  platform: CurrencyPresentationSettings,
  overlay: CurrencyPresentationResponse | null | undefined,
): CurrencyPresentationSettings {
  const src = brokerGoldImageOf(overlay);
  if (!src) return platform;
  const alt = resolveCurrencyPresentation("GOLD", platform).displayName;
  return { ...platform, GOLD: { ...platform.GOLD, icon: { kind: "image", src, alt } } };
}

/**
 * The images to try for a presentation, in order: its own, then — GOLD
 * only — the platform's GOLD image and the bundled coin. Empty when the
 * icon is not an image. RED and GREEN keep their one image (a failed one
 * still degrades to the monogram, as before SM-978).
 */
export function imageSourcesFor(
  presentation: CurrencyPresentation,
  platform: CurrencyPresentationSettings,
): string[] {
  if (presentation.icon.kind !== "image") return [];
  const out = [presentation.icon.src];
  if (presentation.code !== "GOLD") return out;
  for (const settings of [platform, undefined]) {
    const icon = resolveCurrencyPresentation("GOLD", settings).icon;
    if (icon.kind === "image" && !out.includes(icon.src)) out.push(icon.src);
  }
  return out;
}
